/**
 * Sample-based sound effects (docs/architecture.md §10): the CC0 sound set that
 * tools/build-sounds.mjs (npm run sounds) writes to public/sounds/, played with plain Web Audio.
 *
 * - The manifest and every sample are fetched and decoded once per visit, in the background,
 *   after the AudioContext exists. Nothing waits for them.
 * - Each sound falls back to its synthesised version (synth.ts) until its sample is ready, or for
 *   good if it can't load (no AAC decoder, offline), so the game never goes quiet.
 * - Fight sounds get a random variation (never the same twice in a row) and a slight random
 *   pitch, so repeated hits don't sound mechanical.
 */
import { useApp } from '../state/store';
import type { Settings } from '../storage/settings';
import { audioContext, hasUserActivation, unlockAudio } from './context';
import { Synth, type SoundHandle } from './synth';

/** The sound ids of public/sounds/manifest.json (assets/sounds/sounds.json). */
export type SoundId =
  | 'swing'
  | 'swingHeavy'
  | 'hit'
  | 'hitHeavy'
  | 'blocked'
  | 'parry'
  | 'grab'
  | 'slam'
  | 'fenceSlam'
  | 'guardBreak'
  | 'tired'
  | 'charge'
  | 'bell'
  | 'ko'
  | 'dizzy'
  | 'punish'
  | 'popup'
  | 'win'
  | 'lose';

interface SoundManifest {
  version: number;
  /** File names are relative to the manifest. Durations are in seconds, one per file. */
  sounds: Partial<Record<SoundId, { urls: string[]; durations: number[] }>>;
}

interface Sample {
  buffer: AudioBuffer;
  /** Leading near-silence to skip, in seconds (AAC encoder priming, if the decoder kept it). */
  offset: number;
  /** Seconds of sound after `offset`. */
  duration: number;
}

interface Voice {
  src: AudioBufferSourceNode;
  gain: GainNode;
}

interface PlayOptions {
  /** Linear gain (1 = the level the sound set was balanced at). */
  gain?: number;
  /** Playback rate (pitch and speed) before the random spread. */
  rate?: number;
  /** Random rate spread, ± this share. Music and pitched cues use 0. */
  spread?: number;
  /** Delay in seconds. */
  at?: number;
}

const MANIFEST_URL = `${import.meta.env.BASE_URL}sounds/manifest.json`;
const RATE_SPREAD = 0.04;
/** Leading samples quieter than this, relative to the sample's peak, are skipped (−60 dB). */
const SILENCE = 0.001;
/** synth.ts's defaults, which the samples stand in for. */
const BELL_GAIN = 0.26;
const TING_BASE = 1568;
/** The wind-up hum is sped up or slowed down to fit the wind-up, within these rates. */
const CHARGE_RATE_MIN = 0.8;
const CHARGE_RATE_MAX = 1.3;
const CHARGE_FADE = 0.06;
/** Master level under full volume, like the fight's (fightAudio.ts). */
const MASTER = 0.9;

const bank = new Map<SoundId, Sample[]>();
let loading: Promise<void> | null = null;

function canPlayAac(): boolean {
  try {
    return document.createElement('audio').canPlayType('audio/mp4; codecs="mp4a.40.2"') !== '';
  } catch {
    return false;
  }
}

/** Where the sound starts: skips encoder priming and any other leading near-silence. */
function soundStart(buffer: AudioBuffer): number {
  const data = buffer.getChannelData(0);
  let peak = 0;
  for (let i = 0; i < data.length; i++) peak = Math.max(peak, Math.abs(data[i]));
  const threshold = peak * SILENCE;
  let i = 0;
  while (i < data.length && Math.abs(data[i]) <= threshold) i++;
  // Keep 1 ms before the first sound so its attack stays whole.
  return Math.max(0, i - Math.round(buffer.sampleRate * 0.001)) / buffer.sampleRate;
}

async function decode(ctx: BaseAudioContext, url: string, duration: number): Promise<Sample> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${url}: ${res.status}`);
  const buffer = await ctx.decodeAudioData(await res.arrayBuffer());
  const offset = soundStart(buffer);
  // The decoder may add encoder padding at the end; the manifest knows the real length.
  return { buffer, offset, duration: Math.min(duration || Infinity, buffer.duration - offset) };
}

/**
 * Loads the sound set into memory, once per visit. Resolves when done (samples that fail are
 * left out and keep their synthesised sound). A failed manifest fetch is retried on a later call.
 */
export function loadSamples(ctx: BaseAudioContext): Promise<void> {
  loading ??= (async () => {
    if (!canPlayAac()) return;
    let manifest: SoundManifest;
    try {
      const res = await fetch(MANIFEST_URL);
      if (!res.ok) throw new Error(`sound manifest: ${res.status}`);
      manifest = (await res.json()) as SoundManifest;
    } catch {
      loading = null;
      return;
    }
    const base = new URL(MANIFEST_URL, location.href);
    await Promise.all(
      Object.entries(manifest.sounds).map(async ([id, entry]) => {
        if (!entry) return;
        const decoded = await Promise.all(
          entry.urls.map((url, i) =>
            decode(ctx, new URL(url, base).href, entry.durations[i] ?? 0).catch(() => null),
          ),
        );
        const samples = decoded.filter((s): s is Sample => s !== null);
        if (samples.length) bank.set(id as SoundId, samples);
      }),
    );
  })();
  return loading;
}

/** Starts loading the sound set if the AudioContext exists. Safe to call any time, repeatedly. */
export function preloadSamples(): void {
  const ctx = audioContext();
  if (ctx) void loadSamples(ctx);
}

/**
 * Plays the sound set into `out`. Same methods as Synth (the fight's event mapping calls these),
 * plus the newer fight cues and the UI sounds. Any sound whose sample isn't loaded is synthesised.
 */
export class SamplePlayer {
  private readonly ctx: AudioContext;
  private readonly out: AudioNode;
  private readonly synth: Synth;
  private readonly last = new Map<SoundId, number>();

  constructor(ctx: AudioContext, out: AudioNode) {
    this.ctx = ctx;
    this.out = out;
    this.synth = new Synth(ctx, out);
    void loadSamples(ctx);
  }

  // -------------------------------------------------------------------------------------------
  // Building blocks

  /** A random variation, never the one played last time. */
  private pick(id: SoundId): Sample | null {
    const list = bank.get(id);
    if (!list?.length) return null;
    let i = Math.floor(Math.random() * list.length);
    if (list.length > 1 && i === this.last.get(id)) {
      i = (i + 1 + Math.floor(Math.random() * (list.length - 1))) % list.length;
    }
    this.last.set(id, i);
    return list[i] ?? null;
  }

  private voice(sample: Sample, pan: number, o: PlayOptions): Voice {
    const { ctx } = this;
    const src = ctx.createBufferSource();
    src.buffer = sample.buffer;
    const spread = o.spread ?? RATE_SPREAD;
    src.playbackRate.value = (o.rate ?? 1) * (1 + (Math.random() * 2 - 1) * spread);
    const gain = ctx.createGain();
    gain.gain.value = o.gain ?? 1;
    src.connect(gain);
    let panner: StereoPannerNode | null = null;
    if (pan) {
      panner = ctx.createStereoPanner();
      panner.pan.value = Math.max(-1, Math.min(1, pan));
      gain.connect(panner).connect(this.out);
    } else {
      gain.connect(this.out);
    }
    src.onended = () => {
      src.disconnect();
      gain.disconnect();
      panner?.disconnect();
    };
    src.start(ctx.currentTime + (o.at ?? 0), sample.offset, sample.duration);
    return { src, gain };
  }

  /** Plays a sample; false if it isn't loaded, so the caller can synthesise the sound instead. */
  private play(id: SoundId, pan = 0, o: PlayOptions = {}): boolean {
    const sample = this.pick(id);
    if (!sample) return false;
    this.voice(sample, pan, o);
    return true;
  }

  // -------------------------------------------------------------------------------------------
  // Fight sounds (the Synth's methods)

  /** A strike cutting the air; `heavy` for a grapple or signature dash. */
  whoosh(pan: number, heavy = false): void {
    if (!this.play(heavy ? 'swingHeavy' : 'swing', pan)) this.synth.whoosh(pan, heavy);
  }

  /** A clean strike landing (`weight` scales it, e.g. under a block or a counter; `rate` < 1 is deeper). */
  thud(pan: number, weight = 1, rate = 1): void {
    if (!this.play('hit', pan, { gain: weight, rate })) this.synth.thud(pan, weight);
  }

  /** The signature landing. */
  boom(pan: number): void {
    if (!this.play('hitHeavy', pan)) this.synth.boom(pan);
  }

  /** A blocked hit. */
  clack(pan: number): void {
    if (!this.play('blocked', pan)) this.synth.clack(pan);
  }

  /** A parry; a lower `base` (Hz, like the synth's) plays it lower, e.g. Iron chin. */
  ting(pan: number, base = TING_BASE): void {
    if (!this.play('parry', pan, { rate: base / TING_BASE, spread: 0.01 })) {
      this.synth.ting(pan, base);
    }
  }

  /** The grab before a takedown. */
  grab(pan: number): void {
    if (!this.play('grab', pan)) this.synth.grab(pan);
  }

  /** A takedown slam on the mat, or into the fence (`weight` louder, `rate` < 1 deeper). */
  slam(pan: number, fence: boolean, weight = 1, rate = 1): void {
    if (!this.play(fence ? 'fenceSlam' : 'slam', pan, { gain: weight, rate })) this.synth.slam(pan, fence);
  }

  /** A guard broken. */
  crack(pan: number): void {
    if (!this.play('guardBreak', pan)) this.synth.crack(pan);
  }

  /** Not enough stamina (local player only). */
  buzz(pan: number): void {
    if (!this.play('tired', pan)) this.synth.buzz(pan);
  }

  /** Ring bell: `strikes` dings `gap` seconds apart (`gain` as the synth's, 0.26 = normal). */
  bell(strikes: number, gap: number, at = 0, gain = BELL_GAIN): void {
    if (!bank.get('bell')?.length) {
      this.synth.bell(strikes, gap, at, gain);
      return;
    }
    // One bell: the same pitch for every ding of a call.
    const rate = 1 + (Math.random() * 2 - 1) * 0.02;
    for (let i = 0; i < strikes; i++) {
      this.play('bell', 0, { at: at + i * gap, gain: gain / BELL_GAIN, rate, spread: 0 });
    }
  }

  /**
   * The signature wind-up: a rising hum fitted to the `dur` seconds left of the wind-up, so it
   * peaks as the dash starts. stop() fades it out quickly (the dash, or an interrupted move).
   */
  charge(pan: number, dur: number): SoundHandle {
    const sample = this.pick('charge');
    if (!sample) return this.synth.charge(pan, dur);
    const fit = sample.duration / Math.max(dur, 0.01);
    const rate = Math.min(CHARGE_RATE_MAX, Math.max(CHARGE_RATE_MIN, fit));
    const at = Math.max(0, dur - sample.duration / rate);
    const { src, gain } = this.voice(sample, pan, { rate, at, spread: 0 });
    let stopped = false;
    return {
      stop: () => {
        if (stopped) return;
        stopped = true;
        const now = this.ctx.currentTime;
        gain.gain.cancelScheduledValues(now);
        gain.gain.setValueAtTime(gain.gain.value, now);
        gain.gain.linearRampToValueAtTime(0, now + CHARGE_FADE);
        try {
          src.stop(now + CHARGE_FADE + 0.01);
        } catch {
          // already stopped
        }
      },
    };
  }

  // -------------------------------------------------------------------------------------------
  // Newer fight cues (not wired to events yet)

  /** The final blow. */
  ko(pan: number): void {
    if (!this.play('ko', pan)) this.synth.boom(pan);
  }

  /** A fighter rocked: a woozy ringing, about 1 s. */
  dizzy(pan: number): void {
    if (!this.play('dizzy', pan, { spread: 0.02 })) this.synth.dizzy(pan);
  }

  /** A read punished: an extra-heavy hit with a bright accent. */
  punish(pan: number): void {
    if (!this.play('punish', pan)) this.synth.punish(pan);
  }

  // -------------------------------------------------------------------------------------------
  // UI

  /** Level-up or skill point jingle. */
  popup(): void {
    if (!this.play('popup', 0, { spread: 0 })) this.synth.popup();
  }

  /** Result stinger: won. */
  win(): void {
    if (!this.play('win', 0, { spread: 0 })) this.synth.win();
  }

  /** Result stinger: lost. */
  lose(): void {
    if (!this.play('lose', 0, { spread: 0 })) this.synth.lose();
  }
}

// ---------------------------------------------------------------------------------------------
// UI sounds outside the fight

export interface UiSounds {
  popup(): void;
  win(): void;
  lose(): void;
}

let ui: { player: SamplePlayer; master: GainNode } | null = null;

function volumeOf(s: Settings): number {
  return s.muted ? 0 : Math.max(0, Math.min(1, s.sfxVolume));
}

/** The menus' player: the shared AudioContext, its own output, following sfxVolume and mute. */
function uiPlayer(): SamplePlayer | null {
  const settings = useApp.getState().settings;
  if (volumeOf(settings) === 0) return null;
  let ctx = audioContext();
  // Called from a tap or key press this is a user gesture, which may start audio (iOS unlock).
  if ((!ctx || ctx.state !== 'running') && hasUserActivation()) ctx = unlockAudio();
  if (!ctx || ctx.state !== 'running') return null;
  if (!ui) {
    const master = ctx.createGain();
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -10;
    limiter.knee.value = 8;
    limiter.ratio.value = 6;
    limiter.attack.value = 0.002;
    limiter.release.value = 0.2;
    master.connect(limiter).connect(ctx.destination);
    ui = { player: new SamplePlayer(ctx, master), master };
    useApp.subscribe((s) => {
      if (ui) ui.master.gain.value = volumeOf(s.settings) * MASTER;
    });
  }
  ui.master.gain.value = volumeOf(settings) * MASTER;
  return ui.player;
}

const uiApi: UiSounds = {
  popup: () => uiPlayer()?.popup(),
  win: () => uiPlayer()?.win(),
  lose: () => uiPlayer()?.lose(),
};

/**
 * Sounds for the menus, popups and the result screen. Every call is safe: it does nothing while
 * audio can't play (muted, volume 0, or no user gesture yet), and a tap's call can unlock audio.
 */
export function uiSounds(): UiSounds {
  return uiApi;
}
